import React, { useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { ActivityIndicator, View } from "~ui"
import { SafeAreaProvider } from "~ui/safe-area"
import { GestureHandlerRootView } from "~ui/gesture-handler"
import "fast-text-encoding"
import "./i18n"
import "./utils/ignore-warnings"
import { RootStore, RootStoreProvider, setupRootStore } from "./models"
import { ErrorBoundary } from "./screens/error/error-boundary"
import { lightTheme, ThemeProvider } from "./theme/styled"
import MessageModal from "~modals/message-modal"
import { initDB } from "~services/expo-sqlite"
import { getDeviceId } from "~utils"
import AppState from "./components/app-state/app-state"
import { GlobalToast } from "~components/global-toast"
import { AppToaster } from "~components/global-toast/toaster"
import AppRouter from "~router/AppRoutes"
import { DeviceEventEmitter } from "~ui"
import Spinner from "~components/global-spinner"

const App = observer(function App() {
  const [rootStore, setRootStore] = useState<RootStore | undefined>(undefined)
  const [spinner, setSpinner] = useState(false)
  const [spinnerText, setSpinnerText] = useState("")
  const [appIsReady, setAppIsReady] = useState(false)

  useEffect(() => {
    const init = async () => {
      try {
        initDB()
        const store = await setupRootStore()
        await getDeviceId()
        await store.load()
        setRootStore(store)
      } catch (err) {
        if (__DEV__) console.warn("Init error:", err)
      } finally {
        setAppIsReady(true)
      }
    }
    init()

    const eventSpinnerShow = DeviceEventEmitter.addListener("globalSpinnerShow", (text) => {
      if (text) setSpinnerText(String(text))
      setSpinner(true)
    })
    const eventSpinnerHide = DeviceEventEmitter.addListener("globalSpinnerHide", () => setSpinner(false))

    return () => {
      eventSpinnerShow.remove()
      eventSpinnerHide.remove()
    }
  }, [])

  if (!appIsReady || !rootStore) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <ActivityIndicator size="large" />
      </View>
    )
  }

  return (
    <RootStoreProvider value={rootStore}>
      <SafeAreaProvider style={{ flex: 1, minHeight: 0, height: "100%" }}>
        <ErrorBoundary catchErrors={__DEV__ ? "dev" : "always"}>
          <AppRouter>
            <AppState />
            <AppToaster />
            <GlobalToast />
            <MessageModal />
            <Spinner visible={spinner} textContent={spinnerText} />
          </AppRouter>
        </ErrorBoundary>
      </SafeAreaProvider>
    </RootStoreProvider>
  )
})

export default function RootApp() {
  return (
    <GestureHandlerRootView style={{ flex: 1, minHeight: 0, height: "100%" }}>
      <ThemeProvider theme={lightTheme}>
        <App />
      </ThemeProvider>
    </GestureHandlerRootView>
  )
}
